import { Popover, useOpenable } from '@execonline-inc/execonline-ui';
import { flip, shift } from '@floating-ui/react';
import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { useMemo, useCallback } from 'react';
import { ExperienceResource } from '../../../../Types';
import DisplayPopover from '../../../../../../ChatbotPreview/Section/PopoverContent/DisplayPopover';
import CardContent from '../ExperienceCard/CardContent';
import {
  calculatePlacementOffset,
  useProgramPopoverPlacement,
} from '../../../../../../ChatbotPreview/Previewer/Ready/Details/DefaultSection/PopoverContent/Types';
import EditStartDatesModal from '../../../../EditStartDatesModal';
import { useLearningCollection } from '../../../../LearningCollectionContext';

export interface Props {
  experienceResource: ExperienceResource;
  sectionIndex: number;
  editable?: boolean;
}

function PopoverContent({ experienceResource, sectionIndex, editable = false }: Props) {
  const placement = useProgramPopoverPlacement();
  const middleware = useMemo(
    () => [calculatePlacementOffset(placement), flip(), shift()],
    [placement],
  );
  const { isOpen, onOpen: openEditDatesModal, onClose: closeEditDatesModal } = useOpenable();

  // Get scheduled programs and handler from context - data is now stored directly in resourceState
  const { resourceState, handleScheduledProgramToggle } = useLearningCollection();
  const experienceId = experienceResource.payload.id;

  // Find the scheduled program for this experience and get selected program IDs as a Set
  const selectedDateIds = useMemo(() => {
    const scheduledProgram = resourceState.payload.scheduled_programs?.find(
      (sp) => sp.id === experienceId,
    );
    return scheduledProgram ? new Set(scheduledProgram.program_ids) : undefined;
  }, [resourceState.payload.scheduled_programs, experienceId]);

  // Create a handler that wraps the context handler with the correct experienceId
  const onDateSelectionChange = useCallback(
    (programId: number, selected: boolean) => {
      handleScheduledProgramToggle(experienceId, programId, selected);
    },
    [handleScheduledProgramToggle, experienceId],
  );

  return (
    <>
      <Popover
        className="max-w-[20.625rem] text-foreground"
        trigger="hover"
        delay={{ open: 100, close: 0 }}
        returnFocus={false}
        placement={placement}
        middleware={middleware}
        renderTrigger={(ref, getReferenceProps) => {
          // Strip role and problematic ARIA attrs that cause violations for hover-only triggers
          const {
            role,
            'aria-haspopup': ariaHasPopup,
            'aria-expanded': ariaExpanded,
            ...restProps
          } = getReferenceProps();
          return (
            <div ref={ref} {...restProps} className="h-full">
              <CardContent
                experienceResource={experienceResource}
                sectionIndex={sectionIndex}
                selectedDateIds={selectedDateIds}
                onEditDates={openEditDatesModal}
                editable={editable}
              />
            </div>
          );
        }}
        onClose={({ refs }) => {
          fromNullable(refs.domReference.current).do((element) => {
            element.focus({ preventScroll: true });
          });
        }}
      >
        <DisplayPopover experienceResource={experienceResource} />
      </Popover>
      <EditStartDatesModal
        isOpen={isOpen}
        close={closeEditDatesModal}
        experienceResource={experienceResource}
        selectedDateIds={selectedDateIds ?? new Set()}
        onSelectionChange={onDateSelectionChange}
      />
    </>
  );
}

export default observer(PopoverContent);
